import { isDate, isEqual, parseISO } from 'date-fns';
import * as R from 'ramda';
import { flow, pipe } from 'fp-ts/lib/function.js';
import { lookup } from 'fp-ts/lib/Record.js';
import { curry, fromPairs, toPairs } from 'ramda';
import { exists, rejectEmpty, rejectNil } from '../arrays.js';
import { isSome } from 'fp-ts/lib/Option.js';
export const objectKeys = (obj) => Object.keys(obj);
export const objectValues = (obj) => Object.values(obj);
export const objectEntries = (obj) => {
    return Object.entries(obj);
};
export const mapKeys_ = (fn, object) => objectEntries(object).reduce((acc, [key, value]) => {
    return {
        ...acc,
        [fn(key)]: value,
    };
}, {});
// Define the 'mapKeys' function with proper typing
export const mapKeys = (fn, obj) => {
    return R.pipe(
    // Convert object to pairs (array of key-value tuples)
    R.toPairs, 
    // Apply the transformation function to each value in the pair
    R.map(([key, value]) => [fn(key), value]), 
    // Convert the pairs back into an object
    R.fromPairs)(obj);
};
export const mapKeysC = (fn) => (obj) => mapKeys(fn, obj);
// Define the 'mapValues' function with proper typing
export const mapValues = (fn, obj) => {
    return R.pipe(
    // Convert object to pairs (array of key-value tuples)
    R.toPairs, 
    // Apply the transformation function to each value in the pair
    R.map(([key, value]) => [key, fn(value, key)]), 
    // Convert the pairs back into an object
    R.fromPairs)(obj);
};
export const mapValuesC = (fn) => (obj) => {
    return R.pipe(
    // Convert object to pairs (array of key-value tuples)
    R.toPairs, 
    // Apply the transformation function to each value in the pair
    R.map(([key, value]) => [key, fn(value, key)]), 
    // Convert the pairs back into an object
    R.fromPairs)(obj);
};
export const onlyDefinedKeys = (obj) => Object.fromEntries(Object.entries(obj).filter(([_, value]) => {
    if (isPlainObject(value) && Object.keys(value).length === 0)
        return false;
    if (Array.isArray(value) && value.length === 0)
        return false;
    if (typeof value === 'string' && value === '')
        return false;
    if (typeof value === 'undefined')
        return false;
    if (!exists(value))
        return false;
    return true;
}));
/** Reject keys from an object which have undefined values, and omit those
 * properties from the return type
 * This is variant of onlyDefinedKeys, but intened to be used with objects
 * with dynamic keys when you want to remove potentially undefined keys from
 * the final return type */
export const stripUndefinedKeys = (obj) => onlyDefinedKeys(obj);
export const rejectFalsyValues = (obj) => 
// @ts-expect-error
//  These errors have to do with the fact that 'R.filter' can work on arrays OR objects
//  This is worth looking into, and we should write tests for this function
pipe(obj, R.filter((value) => {
    if (!value)
        return value;
    if (typeof value === 'boolean')
        return value;
    return true;
}), 
// @ts-expect-error
mapValuesC((value) => {
    if (typeof value === 'object')
        return rejectFalsyValues(value);
    return value;
}), R.filter((value) => !R.isNil(value)));
export const rejectNilValues = (obj) => 
// @ts-expect-error
//  These errors have to do with the fact that 'R.filter' can work on arrays OR objects
//  This is worth looking into, and we should write tests for this function
pipe(obj, R.filter((value) => !R.isNil(value)), 
// @ts-expect-error
mapValuesC((value) => {
    if (R.is(Array, value))
        return rejectNil(value);
    if (R.is(Date, value))
        return value;
    if (typeof value === 'object')
        return rejectNilValues(value);
    return value;
}), R.filter((value) => !R.isNil(value)));
export const rejectNullValues = (obj) => pipe(
// @ts-expect-error // idk why this is happening?
obj, R.filter((value) => value !== null), mapValuesC((value) => {
    if (R.is(Array, value))
        return rejectNil(value);
    if (R.is(Date, value))
        return value;
    if (typeof value === 'object')
        return rejectNullValues(value);
    return value;
}), R.filter((value) => value !== null));
export const rejectUndefinedValues = (obj) => pipe(obj, (o) => R.filter((v) => v !== undefined, o), mapValuesC((value) => {
    // typeof null === 'object' in JS, so skip null to prevent recursive crash
    if (value === null)
        return value;
    if (R.is(Array, value))
        return rejectNil(value);
    if (R.is(Date, value))
        return value;
    if (typeof value === 'object')
        return rejectUndefinedValues(value);
    return value;
}), (o) => R.filter((v) => v !== undefined, o));
export const hasNoObjectKeys = flow(toPairs, R.all(([_, value]) => typeof value !== 'object'));
export const rejectEmptyValues = (obj) => pipe(obj, rejectNilValues, R.reject(R.isEmpty), mapValuesC((value) => {
    if (R.is(Array, value))
        return rejectEmpty(value);
    if (R.is(Date, value))
        return value;
    if (typeof value === 'object')
        return rejectEmptyValues(value);
    return value;
}), R.reject(R.isEmpty));
export const hasEmptyValue = flow(R.values, (val) => val.some(R.isEmpty));
export const hasNilValue = flow(R.values, (val) => val.some(R.isNil));
const equalsImpl = (a, b) => {
    const isString = R.is(String);
    const isDate = R.is(Date);
    const isArray = R.is(Array);
    const isObject = (val) => R.is(Object, val) && !isArray(val) && !isDate(val);
    // Handle ISO date strings
    if (isString(a) && isDate(b))
        return isEqual(parseISO(a), b);
    if (isString(b) && isDate(a))
        return isEqual(parseISO(b), a);
    if (isDate(a) && isDate(b))
        return isEqual(a, b);
    // Handle arrays
    if (isArray(a) && isArray(b))
        return (a.length === b.length &&
            a.every((item, index) => equalsImpl(item, b[index])));
    // Handle objects
    if (isObject(a) && isObject(b)) {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length)
            return false;
        return aKeys.every((key) => equalsImpl(a[key], b[key]));
    }
    return R.equals(a)(b);
};
export function equals(...args) {
    // arity check, not `b === undefined` — `equals(x, undefined)` is a real call
    return args.length > 1
        ? equalsImpl(args[0], args[1])
        : (b) => equalsImpl(args[0], b);
}
export const getDifference = (comparison) => (desired) => {
    return pipe(desired, R.when(R.has('id'), R.omit(['id'])), R.when(R.has('updatedAt'), R.omit(['updatedAt'])), R.when(R.has('createdAt'), R.omit(['createdAt'])), 
    // @ts-expect-error
    toPairs, R.reject(([key, value]) => {
        if (typeof value === 'object' && !isDate(value))
            return pipe(value || {}, onlyDefinedKeys, rejectEmptyValues, getDifference(pipe(comparison?.[key] || {}, onlyDefinedKeys, rejectEmptyValues)), R.or(R.isEmpty, R.isNil));
        // If the previous value is undefined, sometimes we set it to true, then change our mind and set it to false
        // When this happens, we don't want to show the property as 'modified'
        if (typeof comparison?.[key] === 'undefined' && value === false)
            return true;
        return equals(value, comparison?.[key]);
    }), fromPairs, rejectEmptyValues);
};
// Fixes issues with null values and list comparison in the v1 version
export const getDifferenceV2 = (comparison) => (desired) => {
    return pipe(desired, R.when(R.has('id'), R.omit(['id'])), R.when(R.has('updatedAt'), R.omit(['updatedAt'])), R.when(R.has('createdAt'), R.omit(['createdAt'])), 
    // @ts-expect-error
    toPairs, R.reject(([key, value]) => {
        if (isPlainObject(value)) {
            const reject = pipe(value, R.ifElse(
            // If oldValue is an object
            R.always(isPlainObject(comparison?.[key])), 
            // compare the difference in the new and old objects
            getDifferenceV2(comparison?.[key]), 
            // otherwise new is different, so return the new
            R.always(value)), R.or(R.isEmpty, R.isNil));
            return reject;
        }
        // If the previous value is undefined, sometimes we set it to true, then change our mind and set it to false
        // When this happens, we don't want to show the property as 'modified'
        if (typeof comparison?.[key] === 'undefined' && value === false)
            return true;
        return equals(value, comparison?.[key]);
    }), fromPairs);
};
export const omitFalseValues = (comparison) => (desired) => {
    return pipe(desired, R.toPairs, R.reject(([key, value]) => {
        if (typeof value === 'boolean')
            return value;
        return true;
    }), R.fromPairs);
};
/**
 * Curried deep-equality check. Prefer `equals` (or `R.equals`) — this is kept
 * only for the call sites that already depend on it.
 *
 * ⚠️ Throws `TypeError` when exactly one side is `null` and the other a
 * non-null object/array: `typeof null === 'object'`, so it slips past the
 * primitive check and reaches `Object.keys(null)`. Because it recurses, a
 * `null` nested anywhere beside an object throws too.
 *
 * vs `R.equals`: strictly less correct. It adds an `a === b` reference
 * fast-path, but compares objects/arrays by key count alone — so it treats
 * `[]` as equal to `{}` (and `[1]` as equal to `{ 0: 1 }`) — and it does not
 * coerce ISO date strings.
 *
 * vs `equals`: `equals` is null-safe, keeps arrays distinct from objects, and
 * coerces ISO date strings against `Date`s; `deepEqual` does none of these.
 */
export const deepEqual = curry((a, b) => {
    if (a === b)
        return true;
    if (isDate(a) || isDate(b))
        return isDate(a) && isDate(b) && isEqual(a, b);
    if (typeof a !== 'object' || typeof b !== 'object')
        return R.equals(a, b);
    if (Object.keys(a).length !== Object.keys(b).length)
        return R.equals(a, b);
    for (const key in a) {
        if (!deepEqual(a[key], b[key]))
            return false;
    }
    return true;
});
// Example usage:
const paths = ['dog', 'dog.name', 'cat.breed', 'cow.breed.type'];
// const nestedObject = transformToNestedObject(paths)
function transformToNestedObject(paths) {
    const result = {};
    paths.forEach((path) => {
        const keys = path.split('.');
        if (keys.length === 1) {
            // If there is only one key (no dots), set it directly to true
            result[path] = true;
        }
        else {
            let current = result;
            keys.forEach((key, index) => {
                if (index === keys.length - 1) {
                    current[key] = true;
                }
                else {
                    if (!current[key]) {
                        current[key] = {};
                    }
                    current = current[key];
                }
            });
        }
    });
    return result;
}
// Example usage:
const singleKeyPaths = ['dog'];
const nestedObjectWithSingleKey = transformToNestedObject(singleKeyPaths);
export const hasProps = (keys) => (record) => keys.every((key) => hasProp(key)(record));
export const hasProp = (key) => (record) => pipe(lookup(key, record), isSome);
const mergeKey = (key, obj = {}) => {
    const [head, ...tail] = key.split('.');
    return {
        ...obj,
        [head]: tail.length > 0 ? mergeKey(tail.join('.'), obj[head] || {}) : true,
    };
};
/**
 * Transforms an array of dot-separated keys into a nested object with all values set to `true`.
 */
export const keysToNestedBoolObject = (keys) => R.reduce((acc, key) => mergeKey(key, acc), {}, keys);
/**
 * Recursively traverses a deeply nested object and collects keys in dot-separated format.
 */
export const extractKeys = (obj = {}, prefix = '') => {
    return Object.entries(obj).flatMap(([key, value]) => {
        const fullPath = prefix ? `${prefix}.${key}` : key;
        return value === true
            ? [fullPath] // If the value is `true`, add the key to the result
            : extractKeys(value, fullPath); // Otherwise, recursively process nested objects
    });
};
export const hasModifiedProperties = (previous, updates, omit = []) => {
    return pipe(updates, getDifference(previous), R.omit(omit), R.isEmpty);
};
export const getModifiedProperties = (previous, updates, omit) => {
    return pipe(updates, getDifference(previous), 
    // an id is never modified, so we always omit it
    R.when(R.has('id'), R.omit(['id'])), R.when(R.has('updatedAt'), R.omit(['updatedAt'])), R.when(R.has('createdAt'), R.omit(['createdAt'])), 
    // @ts-expect-error
    R.omit((omit || [])));
};
export const getModifiedPropertiesC = (previous) => (updates, omit = []) => {
    return pipe(updates, getDifference(previous), R.omit(omit));
};
// This is replaced by the 'remeda' version, but we are keeping it here just in case remedas breaks stuff and we need to change it back
const isPlainObject = (value) => !!value && Object.getPrototypeOf(value) === Object.prototype;
/** Returns an empty array of records is not given */
export const getRelationIds = (records, relationKey) => pipe(records || [], R.map(R.prop(relationKey)), R.uniq, rejectNil);
export const indexByRelation = (records, relationKey) => R.indexBy(R.prop(relationKey), records || []);
export const indexById = (records) => R.indexBy(R.prop('id'), records || []);
export const hasKeyRecursive = (obj, key) => {
    if (obj === null || !isPlainObject(obj))
        return false;
    if (Object.keys(obj).some((key) => key === key))
        return true;
    return Object.values(obj).some((value) => hasKeyRecursive(value, key));
};
export const pickBooleansDeepFlat = (input) => {
    const step = (obj) => Object.entries(obj).reduce((acc, [key, value]) => {
        if (typeof value === 'boolean')
            return { ...acc, [key]: value };
        if (isPlainObject(value))
            return { ...acc, ...step(value) };
        return acc;
    }, {});
    return isPlainObject(input) ? step(input) : {};
};
//# sourceMappingURL=objects.js.map